// 1. React
import { memo, useEffect, useMemo } from "react";

// 2. Helpers
import { formatCurrency, useTranslate } from "helpers";

// 3. Types
import { OfferItemFormProps } from "./types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { FleetVehicleOfferRentItemEditFormValues } from "services/api/fleet/vehicles/offers/types";

// 4. Form hooks
import {
    useHandleDropdownChange,
    useHandleNumberFieldChange,
    useHandleTextFieldChange,
} from "services/forms/hooks";

// 5. API / Consts
import { DiscountType, TAX_RATE_OPTIONS } from "services/api/settings";

// 6. UI Components
import { TextField } from "ui/components/TextField/TextField";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { Button } from "ui/components/Button/Button";
import { TextArea } from "ui/components/TextField/TextArea";
import { NumberField } from "ui/components/TextField/NumberField";
import BillingItemNameField from "ui/components/Dropdown/BillingItemNameField/BillingItemNameField";
import UnitOfMeasureDropdown from "ui/components/Dropdown/UnitOfMeasureDropdown/UnitOfMeasureDropdown";
import KpdDropdown from "ui/components/Dropdown/KpdDropdown/KpdDropdown";
import { BillingItemType } from "services/api/finance/billing/types";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";


const OfferItemForm: React.FC<OfferItemFormProps> = (props) => {
    const { show, onHide, itemFormAdd, setItemFormAdd, errors, isEdit, onSubmit } = props;

    const { t } = useTranslate();

    /* ------------------------------------------------------------------ */
    /* DROPDOWN OPTIONS */
    /* ------------------------------------------------------------------ */

    const taxRateItems = useMemo<DropdownOption[]>(() => {
        return TAX_RATE_OPTIONS.map((item) => ({
            text: item.label,
            value: item.value.toString(),
        }));
    }, []);

    const discountTypeItems: DropdownOption[] = Object.entries(DiscountType).map(
        ([_, value]) => ({
            value: value.key,
            text: value.label,
        })
    );

    /* ------------------------------------------------------------------ */
    /* TOTAL CALCULATION */
    /* ------------------------------------------------------------------ */

    useEffect(() => {
        const total = calculateOfferItemTotal(itemFormAdd);

        setItemFormAdd(prev => ({
            ...prev,
            total,
        }));
    }, [
        itemFormAdd.quantity,
        itemFormAdd.priceWithoutTax,
        itemFormAdd.taxRate,
        itemFormAdd.discountTypeId,
        itemFormAdd.discountAmount,
    ]);

    const calculateOfferItemTotal = (
        values: FleetVehicleOfferRentItemEditFormValues
    ): number => {
        const {
            quantity,
            priceWithoutTax,
            taxRate,
            discountTypeId,
            discountAmount,
        } = values;

        if (!quantity || !priceWithoutTax || taxRate === null || taxRate === undefined) {
            return 0;
        }

        // Popust ide PRIJE PDV-a — isto pravilo koristi poslužitelj (BillingTotalsCalculator),
        // PDF i UBL; drugačiji redoslijed davao bi drugi iznos na dokumentu nego u obrascu.
        const base = quantity * priceWithoutTax;

        let net = base;

        if (discountAmount && discountAmount > 0) {
            if (discountTypeId === DiscountType.PERCENTAGE.key) {
                net = base * (1 - discountAmount / 100);
            }

            if (discountTypeId === DiscountType.AMOUNT.key) {
                net = base - discountAmount;
            }
        }

        const total = net * (1 + Number(taxRate) / 100);

        return Math.max(0, Number(total.toFixed(2)));
    };

    /* ------------------------------------------------------------------ */
    /* HANDLERS */
    /* ------------------------------------------------------------------ */

    const handleDropdownChange = useHandleDropdownChange(
        setItemFormAdd,
        itemFormAdd
    );

    const handleTextFieldChange = useHandleTextFieldChange(
        setItemFormAdd,
        itemFormAdd
    );

    const handleNumberFieldChange = useHandleNumberFieldChange(
        setItemFormAdd,
        itemFormAdd
    );

    const handleTaxRateChange = (
        e: any,
        name: string,
        item: DropdownOption
    ) => {
        const taxRate = Number(item.value);

        // ako nema poreza, samo setiraj taxRate
        if (!taxRate) {
            setItemFormAdd(prev => ({
                ...prev,
                taxRate,
            }));
            return;
        }

        const taxMultiplier = 1 + taxRate / 100;

        setItemFormAdd(prev => {
            // PRIORITET: ako postoji priceWithoutTax → iz njega računamo
            if (prev.priceWithoutTax) {
                return {
                    ...prev,
                    taxRate,
                    priceWithTax: Number(
                        (Number(prev.priceWithoutTax) * taxMultiplier).toFixed(2)
                    ),
                };
            }

            // ako nema priceWithoutTax, ali ima priceWithTax → računaj obrnuto
            if (prev.priceWithTax) {
                return {
                    ...prev,
                    taxRate,
                    priceWithoutTax: Number(
                        (Number(prev.priceWithTax) / taxMultiplier).toFixed(2)
                    ),
                };
            }

            // fallback – samo set taxRate
            return {
                ...prev,
                taxRate,
            };
        });
    };

    const handlePriceWithoutTaxChange = (value: number | null, name?: string) => {
        if (!itemFormAdd.taxRate || value === null) {
            setItemFormAdd(prev => ({
                ...prev,
                priceWithoutTax: value,
            }));
            return;
        }

        const taxMultiplier = 1 + Number(itemFormAdd.taxRate) / 100;
        const priceWithTax = Number((value * taxMultiplier).toFixed(2));

        setItemFormAdd(prev => ({
            ...prev,
            priceWithoutTax: value,
            priceWithTax,
        }));
    };

    const handlePriceWithTaxChange = (value: number | null, name?: string) => {
        if (!itemFormAdd.taxRate || value === null) {
            setItemFormAdd(prev => ({
                ...prev,
                priceWithTax: value,
            }));
            return;
        }

        const taxMultiplier = 1 + Number(itemFormAdd.taxRate) / 100;
        const priceWithoutTax = Number((value / taxMultiplier).toFixed(2));

        setItemFormAdd(prev => ({
            ...prev,
            priceWithTax: value,
            priceWithoutTax,
        }));
    };


    /* ------------------------------------------------------------------ */
    /* VRSTA STAVKE IZ ŠIFRARNIKA */
    /* ------------------------------------------------------------------ */

    // Odabir vrste popuni redak zadanim vrijednostima; prazna zadana vrijednost ne briše
    // ono što je korisnik već upisao, a sve ostaje uređivo.
    // Slobodno upisan naziv — stavka gubi vrstu iz šifrarnika, ostalo ostaje kako je upisano.
    const handleItemNameChange = (name: string) => {
        setItemFormAdd(prev => ({
            ...prev,
            name,
            billingItemTypeId: null,
        }));
    };

    const handleItemTypeSelect = (itemType: BillingItemType | null) => {
        if (!itemType) return;

        setItemFormAdd(prev => {
            const taxRate = itemType.defaultTaxRate ?? prev.taxRate;
            const priceWithoutTax = itemType.defaultPriceWithoutTax ?? prev.priceWithoutTax;
            const taxMultiplier = 1 + Number(taxRate ?? 0) / 100;

            return {
                ...prev,
                billingItemTypeId: itemType.id,
                code: itemType.code,
                name: itemType.name,
                taxRate,
                quantity: itemType.defaultQuantity ?? prev.quantity,
                unitOfMeasureId: itemType.defaultUnitOfMeasureId ?? prev.unitOfMeasureId,
                kpdCode: itemType.defaultKpdCode ?? prev.kpdCode,
                priceWithoutTax,
                priceWithTax: priceWithoutTax
                    ? Number((Number(priceWithoutTax) * taxMultiplier).toFixed(2))
                    : prev.priceWithTax,
                note: itemType.defaultNote ?? prev.note,
            };
        });
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            position="centered"
            size="medium"
        >
            <ModalHeader
                icon="/media/images/add-icon.svg"
                title={isEdit
                    ? t("admin.fleet.vehicle.offer.rent.item.edit.title")
                    : t("admin.fleet.vehicle.offer.rent.item.add.title")
                }
                onHide={onHide}
            />
            <ModalBody>
                {show && (
                    <>
                        <div className="mb-3 p-3">
                            <div className="flex gap-3 mb-3">
                                <div className="flex-1 max-w-[90px] pt-3">
                                    <TextField
                                        label={t("admin.fleet.vehicle.offer.rent.item.add.code")}
                                        type="text"
                                        name="code"
                                        onChange={handleTextFieldChange}
                                        value={itemFormAdd.code}
                                        error={errors.code}
                                        required
                                    />
                                </div>
                                <div className="flex-1 max-w-[150px] pt-3">
                                    <KpdDropdown
                                        name="kpdCode"
                                        label="KPD oznaka"
                                        value={itemFormAdd.kpdCode}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                                <div className="flex-1 min-w-[260px] pt-3">
                                    <BillingItemNameField
                                        billingItemTypeId={itemFormAdd.billingItemTypeId}
                                        itemName={itemFormAdd.name}
                                        onItemTypeSelect={handleItemTypeSelect}
                                        onFreeTextChange={handleItemNameChange}
                                        error={errors.name}
                                        required
                                    />
                                </div>
                                <div className="flex-1 max-w-[80px] pt-3">
                                    <Dropdown
                                        name="taxRate"
                                        label={t("admin.fleet.vehicle.offer.rent.item.add.taxrate")}
                                        options={taxRateItems}
                                        onChange={handleTaxRateChange}
                                        value={itemFormAdd.taxRate?.toString() ?? null}
                                        error={errors.taxRate}
                                        required
                                    />
                                </div>
                                <div className="max-w-[65px] pt-3">
                                    <NumberField
                                        label={t("admin.fleet.vehicle.offer.rent.item.add.quantity")}
                                        name="quantity"
                                        onChange={handleNumberFieldChange}
                                        value={itemFormAdd.quantity}
                                        error={errors.quantity}
                                        decimals={0}
                                        min={1}
                                        step={1}
                                        required
                                    />
                                </div>
                                <div className="max-w-[140px] pt-3">
                                    <UnitOfMeasureDropdown
                                        name="unitOfMeasureId"
                                        label="Jedinica mjere"
                                        value={itemFormAdd.unitOfMeasureId}
                                        onChange={handleDropdownChange}
                                    />
                                </div>
                                <div className="max-w-[120px] pt-3">
                                    <NumberField
                                        label={t("admin.fleet.vehicle.offer.rent.item.add.pricewithouttax")}
                                        name="priceWithoutTax"
                                        onChange={handlePriceWithoutTaxChange}
                                        value={itemFormAdd.priceWithoutTax}
                                        error={errors.priceWithoutTax}
                                        required
                                    />
                                </div>
                                <div className="max-w-[120px] pt-3">
                                    <NumberField
                                        label={t("admin.fleet.vehicle.offer.rent.item.add.pricewithtax")}
                                        name="priceWithTax"
                                        onChange={handlePriceWithTaxChange}
                                        value={itemFormAdd.priceWithTax}
                                        error={errors.priceWithTax}
                                        required
                                    />
                                </div>
                                <div className="flex-1 max-w-[120px]">
                                    <div className="flex flex-col">
                                        <div className="w-[120px]">
                                            <Dropdown
                                                name="discountTypeId"
                                                options={discountTypeItems}
                                                onChange={handleDropdownChange}
                                                value={itemFormAdd.discountTypeId}
                                                error={errors.discountTypeId}
                                                required
                                            />
                                        </div>
                                        <div className="w-[120px]">
                                            <NumberField
                                                name="discountAmount"
                                                onChange={handleNumberFieldChange}
                                                value={itemFormAdd.discountAmount}
                                                error={errors.discountAmount}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end items-baseline gap-2 mb-3">
                                <span className="text-sm text-slate-500">{t("admin.fleet.vehicle.offer.rent.item.add.total")}:</span>
                                <span className="text-base font-semibold text-slate-900">
                                    {formatCurrency.format(itemFormAdd.total ?? 0)}
                                </span>
                            </div>
                            <div className="mb-3">
                                <TextArea
                                    name="note"
                                    onChange={handleTextFieldChange}
                                    value={itemFormAdd.note}
                                    placeholder={t("admin.fleet.vehicle.offer.rent.item.add.note.placeholder")}
                                />
                            </div>
                        </div>
                        <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                            <Button
                                variant="outlined"
                                children={t("admin.close")}
                                onClick={onHide}
                            />
                            <Button
                                variant="primary"
                                children={isEdit ? t("admin.save") : t("admin.fleet.vehicle.offer.rent.item.add.button")}
                                onClick={onSubmit}
                            />
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

export default memo(OfferItemForm);
